import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';

import * as graphql from 'generated/graphql';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';

import BulkModal from './BulkModal';

export default function BulkRemoveCycleModal(props: {
	users: graphql.User[];
	onDismiss: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const client = useApolloClient();

	const cycles = graphql.useCycleTemplatesQuery({
		variables: {
			isArchived: false,
			first: 100,
		},
	});

	const [cycleTemplateID, setCycleTemplateID] = useState<string | undefined>();

	const [removeCycleInstance] = graphql.useRemoveCycleInstanceMutation();

	const options = cycles.data?.cycleTemplates.edges.map(({ node }) => ({
		label: localize(node.title).text,
		value: node.id,
	}));
	options?.sort((a, b) => a.label.localeCompare(b.label));

	return (
		<BulkModal
			name="bulkRemoveCycle"
			valid
			apply={async (user) => {
				const response = (await client.query({
					query: graphql.UserCyclesDocument,
					variables: { id: user.id },
				})) as graphql.UserCyclesQueryResult;

				let deleted = 0;

				for (const { node: cycle } of response.data?.user.cycles.edges ?? []) {
					if (cycle.cycleTemplate.id !== cycleTemplateID) {
						continue;
					}

					await removeCycleInstance({ variables: { input: { id: cycle.id } } });

					deleted += 1;
				}

				return deleted;
			}}
			users={props.users}
			onDismiss={props.onDismiss}
		>
			<Label title={t('employees.bulkRemoveCycle.cycle.label')} />
			{cycles.data ? (
				<Select options={options} onChange={(option) => setCycleTemplateID(option!.value)} />
			) : (
				<Spinner isLoading size="18px" />
			)}
		</BulkModal>
	);
}
